import { ComponentType, Fragment } from "react"
import { Box, Divider, SxProps, Typography } from "@mui/material"
import _ from "lodash"
import { ImdfFeature } from "providers/venue/types"
import { useTranslation } from "providers/i18n"
import {
  DiningCard as DefaultDiningCard,
  IDiningCardProps,
} from "components/domains/dining/DiningCard"
import { formatLevelName } from "utils/feature"

interface IDiningGroupLayoutProps {
  pages: Array<{ data: ImdfFeature[] }>
  gridItemSx?: SxProps
  levelHeaderSx?: SxProps
  dividerSx?: SxProps
  diningCardSx?: SxProps
  DiningCard?: ComponentType<IDiningCardProps>
}

export const DiningGroupLayout = ({
  pages,
  gridItemSx,
  levelHeaderSx,
  dividerSx,
  diningCardSx,
  DiningCard = DefaultDiningCard,
}: IDiningGroupLayoutProps) => {
  const { t } = useTranslation()
  const renderedLevels = new Set<string>()

  return (
    <>
      {pages.map((page, pageNum) => {
        // Group restaurants by level_name
        const groupedByLevel = _.groupBy(page.data, "properties.level_name")
        // Sort groups by ordinal (descending)
        const sortedGroups = _.orderBy(
          Object.entries(groupedByLevel),
          ([, restaurants]) => _.get(restaurants, "[0].properties.ordinal", 0),
          ["desc"]
        )

        return (
          <Fragment key={pageNum}>
            {sortedGroups.map(([level, restaurants]) => {
              const shouldShowHeader = !renderedLevels.has(level)
              if (shouldShowHeader) {
                renderedLevels.add(level)
              }

              const levelKey = formatLevelName(level)

              return (
                <Fragment key={`${pageNum}-${level}`}>
                  {shouldShowHeader && (
                    <Box sx={levelHeaderSx}>
                      <Typography>{t(`Level Name.${levelKey}`)}</Typography>
                      <Divider sx={dividerSx} />
                    </Box>
                  )}
                  {restaurants.map((restaurant, index) => {
                    const isNewShop = restaurant?.properties?.isNewShop
                    const isComingSoon = restaurant?.properties?.isComingSoon

                    return (
                      <Box
                        sx={gridItemSx}
                        key={`dining-${pageNum}-${level}-${index}`}
                      >
                        <DiningCard
                          data={restaurant}
                          sx={diningCardSx}
                          isNewShop={isNewShop}
                          isComingSoon={isComingSoon}
                        />
                      </Box>
                    )
                  })}
                </Fragment>
              )
            })}
          </Fragment>
        )
      })}
    </>
  )
}
